/**
 * The one path from a creature spec to pixels — `buildEntity`'s opposite number.
 *
 * The structure builder splits ground from body because isometric occlusion
 * demands it: every pad must sit under every silhouette. A creature has no pad,
 * no contact shadow and no tile, so it is one layer, and the split that matters
 * instead is between what a SPECIES draws (three poses) and what the BUILDER
 * applies (markers, the flash, the dissolve, the outline, the cloak).
 *
 * That split is the whole reason the file exists. Nineteen species cannot drift
 * apart on what armour, dying or being hit looks like if none of them draws it.
 */

import { faunaRamp, signatureRamp, type DepthPalette } from '../render/palette.ts';
import { applyOutline, silhouette } from '../render/primitives.ts';
import { PixelSurface, type Color } from '../render/surface.ts';
import {
  glowStep,
  SIZE_BOXES,
  type CreatureContext,
  type CreaturePaint,
  type CreatureSpec,
  type PoseState,
} from './creature.ts';
import { armored, cloak, regen, swift } from './markers.ts';
import { step } from './organic.ts';

export interface CreatureRender {
  surface: PixelSurface;
  /** The swim line: the point the sim's position maps to. */
  ax: number;
  ay: number;
  /** Draw operations spent — feeds `perfBudget` (creature cap: 400). */
  ops: number;
}

/** Room past the size box for the outline and the marker overlays. */
const PAD = 6;

export function buildCreature(spec: CreatureSpec, ctx: CreatureContext): CreatureRender {
  const box = SIZE_BOXES[spec.size];
  const s = new PixelSurface(box.reach * 2 + PAD * 2, box.rise + box.drop + PAD * 2);
  const anchor = { ax: box.reach + PAD, ay: PAD + box.rise };

  // `hurt` and `death` are the idle animal plus a transform, so the species is
  // asked for a pose it actually authored and the transform lands afterwards.
  const pose: PoseState = ctx.state === 'idle' || ctx.state === 'move' || ctx.state === 'attack'
    ? ctx.state
    : 'idle';
  spec.draw(s, anchor, { ...ctx, state: pose }, paintFor(ctx));

  // Doc 10's property markers, from the content flags. Order matters: bands
  // recolour flesh, so they run before anything adds ink that is not flesh.
  if (spec.markers.armored) armored(s, anchor);
  if (spec.markers.swift) swift(s, anchor, ctx.phase);
  if (spec.markers.regen) regen(s, anchor, ctx.depth, ctx.phase);

  let out = s;
  if (ctx.state === 'hurt') out = flash(s, ctx.depth);
  else if (ctx.state === 'death') out = dissolve(s, ctx.phase);

  applyOutline(out);
  // The transform and the outline are whole-surface passes: one op each, which
  // is what they cost the frame (doc 16 §4's clarification).
  const ops = s.ops + 2;

  // The cloak goes over the finished sprite, outline included: doc 06 wants the
  // player to see a shape they cannot shoot, not a shape with a solid ring.
  if (ctx.hidden && spec.markers.cloaked) {
    return { surface: cloak(out, ctx.phase), ax: anchor.ax, ay: anchor.ay, ops: ops + 1 };
  }
  return { surface: out.resetOps(), ax: anchor.ax, ay: anchor.ay, ops };
}

/** The colours a species is handed for this state. */
export function paintFor(ctx: CreatureContext): CreaturePaint {
  const g = signatureRamp(ctx.depth.enemyGlow);
  const lit: Record<'off' | 'dim' | 'on' | 'hot', Color> = {
    off: g.dim,
    dim: g.dim,
    on: g.on,
    hot: g.hot,
  };
  const stepId = glowStep(ctx.state, ctx.riled);
  return { ramp: faunaRamp(), glow: lit[stepId], halo: g.halo };
}

/**
 * The hit flash (doc 10's damaged row, as an animal reads it): the whole body
 * goes to ink for one frame. A creature has no panel lights to blink and no
 * crack overlay to wear — the flash IS the feedback, and it is the same white
 * on every species so a player never has to learn a second one.
 */
function flash(s: PixelSurface, depth: DepthPalette): PixelSurface {
  return silhouette(s, depth.uiInk);
}

/**
 * Death, in four steps: the light is already out (`glowStep`), the body drops
 * to flat dark, and a quarter of it leaves on every step.
 *
 * The colours are `belly`, `fin` and `maw` rather than a fade, because a fade
 * invents pixels nobody chose and blows Law L5's budget on a frame that lasts
 * an eighth of a second.
 */
function dissolve(s: PixelSurface, phase: number): PixelSurface {
  const f = faunaRamp();
  const k = step(phase, 4);
  const tone = [f.belly, f.fin, f.fin, f.maw][k]!;
  const out = new PixelSurface(s.width, s.height);
  for (let y = 0; y < s.height; y++) {
    for (let x = 0; x < s.width; x++) {
      if (s.alphaAt(x, y) === 0) continue;
      // A stable per-pixel order, so the same body always comes apart the same way.
      if ((Math.imul(x * 73856093 + y * 19349663, 83492791) >>> 29) % 4 < k) continue;
      out.px(x, y, tone, 'set');
    }
  }
  return out;
}
